import CortileSymbolCream from '@/assets/cortile/symbol-cream.svg';
import {useIsMobile} from '@ark/web';
import {Box, Link, Typography} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {CortileBrandPanel} from './CortileBrandPanel';
import {createGatherField, GatherField} from './gatherField';
import {CORTILE, FONT} from './tokens';

type Props = {
  stepKey: string;
  fieldRef?: React.MutableRefObject<GatherField | null>;
  children: React.ReactNode;
};

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false,
  );
  useEffect(() => {
    const query = window.matchMedia?.(REDUCED_MOTION_QUERY);
    if (!query) return;
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

export const CortileAuthLayout = ({stepKey, fieldRef, children}: Props) => {
  const isMobile = useIsMobile('md');
  const stepEl = useRef<HTMLDivElement | null>(null);
  const layerEl = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const reducedMotionRef = useRef(reducedMotion);
  reducedMotionRef.current = reducedMotion;

  useEffect(() => {
    const layer = layerEl.current;
    const field =
      layer && !reducedMotion
        ? createGatherField(layer, CortileSymbolCream, 13)
        : null;
    if (fieldRef) fieldRef.current = field;
    return () => {
      field?.destroy();
      if (fieldRef) fieldRef.current = null;
    };
  }, [isMobile, reducedMotion, fieldRef]);

  useEffect(() => {
    const el = stepEl.current;
    if (!reducedMotionRef.current) {
      el?.animate?.(
        [
          {opacity: 0, transform: 'translateY(8px)'},
          {opacity: 1, transform: 'none'},
        ],
        {duration: 260, easing: 'cubic-bezier(.2,.7,.3,1)'},
      );
    }
    el?.querySelector('input')?.focus({preventScroll: true});
  }, [stepKey]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        bgcolor: CORTILE.cream,
        color: CORTILE.slate,
        fontFamily: FONT,
        display: 'grid',
        gridTemplateColumns: isMobile
          ? '1fr'
          : 'min(44%, 600px) minmax(0, 1fr)',
        overflow: 'hidden',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      {!isMobile && (
        <CortileBrandPanel layerRef={layerEl} reducedMotion={reducedMotion} />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: 3,
          pt: 6,
          pb: 3.5,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Box
            ref={stepEl}
            sx={{
              width: '100%',
              maxWidth: 380,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {children}
          </Box>
        </Box>
        <Typography
          sx={{m: 0, fontFamily: FONT, fontSize: 14, color: CORTILE.slate}}
        >
          New to Cortile?{' '}
          <Link
            href="https://cortile.com/?utm_source=app&utm_medium=referral&utm_campaign=login_page"
            target="_blank"
            rel="noopener"
            sx={{
              fontWeight: 600,
              color: CORTILE.error,
              textUnderlineOffset: 3,
              '&:hover': {color: CORTILE.terracottaDark},
            }}
          >
            See what it does →
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};
